/**
 * Dashboard Page
 * Main dashboard with statistics and charts
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Users,
  Wallet,
  PiggyBank,
  TrendingUp,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

import {
  fetchDashboardStats,
  fetchDashboardCharts,
  fetchRecentActivities,
} from '../dashboardSlice';
import { formatCurrency, formatCompactCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';
import { CardSkeleton } from '../../../components/common/Loader/Loader';

/**
 * Stat Card Component
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {number} props.value - Card value
 * @param {string} props.icon - Icon component
 * @param {string} props.trend - Trend direction
 * @param {number} props.trendValue - Trend value
 * @param {string} props.color - Card color
 */
const StatCard = ({ title, value, icon: Icon, trend, trendValue, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    yellow: 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {typeof value === 'number' && value > 999999
              ? formatCompactCurrency(value)
              : typeof value === 'number'
              ? formatCurrency(value, { showSymbol: false })
              : value}
          </p>
          {trend && (
            <div className="flex items-center mt-2">
              {trend === 'up' ? (
                <ArrowUpRight className="w-4 h-4 text-green-500" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-red-500" />
              )}
              <span
                className={`text-sm ${
                  trend === 'up' ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {trendValue}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

/**
 * Activity Item Component
 * @param {Object} props - Component props
 * @param {Object} props.activity - Activity data
 */
const ActivityItem = ({ activity }) => {
  const icons = {
    member: Users,
    loan: Wallet,
    savings: PiggyBank,
    payment: TrendingUp,
    default: AlertCircle,
  };

  const Icon = icons[activity.type] || icons.default;

  return (
    <div className="flex items-start space-x-3 py-3">
      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {activity.description}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(activity.timestamp)}
        </p>
      </div>
    </div>
  );
};

/**
 * Dashboard Page Component
 * @returns {JSX.Element} Dashboard page
 */
const DashboardPage = () => {
  const dispatch = useDispatch();
  const { stats, activities, isLoading } = useSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchDashboardCharts());
    dispatch(fetchRecentActivities());
  }, [dispatch]);

  if (isLoading && !stats.totalMembers) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Welcome back! Here's what's happening with your SACCOS.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Members"
          value={stats.totalMembers}
          icon={Users}
          trend="up"
          trendValue={12}
          color="blue"
        />
        <StatCard
          title="Total Loans"
          value={stats.totalLoans}
          icon={Wallet}
          trend="up"
          trendValue={8}
          color="green"
        />
        <StatCard
          title="Total Savings"
          value={stats.totalSavings}
          icon={PiggyBank}
          trend="up"
          trendValue={15}
          color="yellow"
        />
        <StatCard
          title="Total Shares"
          value={stats.totalShares}
          icon={TrendingUp}
          trend="up"
          trendValue={5}
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Active Loans
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {stats.activeLoans}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Pending Applications
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {stats.pendingApplications}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Monthly Collections
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {formatCurrency(stats.monthlyCollections)}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Activity
          </h2>
        </div>
        <div className="px-6 py-4">
          {activities.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {activities.slice(0, 5).map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              No recent activity
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
